import React, { useContext, useMemo, useState } from "react";
import clsx from "clsx";
import { FieldArray, Formik, FormikHelpers } from "formik";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { HvGrid } from "@hitachivantara/uikit-react-core";

import { useSnackbar } from "lib/provider/SnackbarProvider";
import usePreventNavigation from "lib/hooks/usePreventNavigation";
import { addWorkflow, useTimeSettings } from "lib/api";
import Container from "components/layout/Container";
import Loading from "components/layout/Loading";
import Banner from "components/common/Banner";
import { FormHeader } from "components/form";
import Details from "components/workflow/AddEdit/Details";
import Trigger from "components/workflow/AddEdit/Trigger";
import EventType from "components/workflow/AddEdit/EventType";
import Conditions from "components/workflow/AddEdit/Conditions";
import Actions from "components/workflow/AddEdit/Actions";
import WorkflowSwitch from "components/workflow/WorkflowSwitch";
import ConfirmationDialog from "components/common/ConfirmationDialog";

import { canBeActivate } from "components/workflow/WorkflowSwitch/WorkflowSwitch";

import { ModuleNames, ModulePolicyTypes } from "lib/api/userSettings";
import { UserDataContext } from "lib/context/UserDataContext";
import NotFound from "components/common/NotFound";
import useStyles from "./styles";
import {
  formDataToWorkflow,
  useInitialValues,
  useSessions,
  useValidationSchema,
} from "./common";

const WORKFLOW = "/settings/workflow";

const WorkflowAdd: React.FC = () => {
  const { t } = useTranslation("workflowAddEdit");
  const history = useHistory();
  const classes = useStyles();
  const [, setPreventNavigation] = usePreventNavigation();
  const [showBanner, setShowBanner] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [submitInactive, setSubmitInactive] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const sections = useSessions(t);
  const { timezone } = useTimeSettings(false);
  const validationSchema = useValidationSchema(t);
  const initialValues = useInitialValues(
    undefined,
    undefined,
    undefined,
    undefined
  );

  const onSave = (data: WorkflowFormData) => {
    return addWorkflow(formDataToWorkflow(data));
  };

  const onSubmitHandler = async (
    values: WorkflowFormData,
    actions: FormikHelpers<WorkflowFormData>
  ) => {
    try {
      await onSave(values);
      enqueueSnackbar(t("validation.success.add"), { variant: "success" });
      setPreventNavigation(false);
      actions.setStatus({});
      history.push(WORKFLOW);
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const errorCode = error?.response?.data?.error?.code;
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const errorStatus = error?.response?.status;
      const suppliedName = values.name;

      if (errorCode?.endsWith("_name_already_taken") || errorStatus === 409) {
        actions.setStatus({
          serverSideErrors: {
            name: t("validation.nameAlreadyTaken", { suppliedName }),
          },
        });
      } else {
        setShowBanner(true);
      }
    }

    setSubmitInactive(false);
  };

  return (
    <Formik
      initialStatus={{ serverSideErrors: {} }}
      initialValues={initialValues as WorkflowFormData}
      validationSchema={validationSchema}
      onSubmit={onSubmitHandler}
    >
      {(props) => {
        const { handleSubmit, isSubmitting, values } = props;
        const goBackHandler = () => {
          history.goBack();
        };

        const onBannerCloseHandler = () => {
          setShowBanner(false);
        };

        const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
          e.preventDefault();
          const v = values as WorkflowFormData;
          if (
            !values.active &&
            !canBeActivate(v.conditions, v.actions, timezone) &&
            !submitInactive
          ) {
            setIsDialogOpen(true);
            return;
          }

          handleSubmit(e);
        };

        const onConfirmHandler = () => {
          setIsDialogOpen(false);
          setSubmitInactive(true);

          handleSubmit();
        };

        const onCancelHandler = () => {
          setSubmitInactive(false);
          setIsDialogOpen(false);
        };

        return (
          <Container className={classes.container} maxWidth="xl">
            {isSubmitting && (
              <Loading
                delay={500}
                loadingLabel={t("submiting")}
                className={clsx(classes.loading, classes.zIndex)}
              />
            )}
            <Banner
              open={showBanner}
              variant="error"
              showIcon
              label={t("validation.unknownError.add")}
              onClose={onBannerCloseHandler}
            />
            <form onSubmit={onSubmit} noValidate>
              <ConfirmationDialog
                isOpen={isDialogOpen}
                onConfirm={onConfirmHandler}
                onCancel={onCancelHandler}
                title={t("validation.inactive.title")}
                message={t("validation.inactive.message")}
                confirmButtonLabel={t("validation.inactive.confirmBtn")}
                cancelButtonLabel={t("validation.inactive.cancelBtn")}
                closeButtonTooltip={t("validation.inactive.closeBtn")}
              />
              <FormHeader
                title={t("title")}
                sections={sections}
                onCancel={goBackHandler}
              >
                <WorkflowSwitch newWorkflowMode />
              </FormHeader>
              <HvGrid container spacing={0}>
                <HvGrid
                  className={classes.sectionMarginCondition}
                  tabIndex={-1}
                  id={sections[0].value}
                  xs={12}
                  item
                >
                  <Details />
                </HvGrid>
                <HvGrid
                  className={classes.sectiomMarginOverride}
                  tabIndex={-1}
                  id={sections[1].value}
                  xs={12}
                  item
                >
                  <Trigger />
                </HvGrid>
                <HvGrid
                  className={classes.sectionMargin}
                  tabIndex={-1}
                  id={sections[2].value}
                  xs={12}
                  item
                >
                  <EventType />
                </HvGrid>
                <HvGrid
                  className={classes.sectionMarginCondition}
                  tabIndex={-1}
                  id={sections[3].value}
                  xs={12}
                  item
                >
                  <Conditions />
                </HvGrid>
                <HvGrid
                  className={classes.sectionMargin}
                  tabIndex={-1}
                  id={sections[4].value}
                  xs={12}
                  item
                >
                  <FieldArray
                    name="actions"
                    render={(arrayHelpers) => (
                      <Actions
                        selectedEventTypeId={values.eventTypeId}
                        {...arrayHelpers}
                      />
                    )}
                  />
                </HvGrid>
              </HvGrid>
            </form>
          </Container>
        );
      }}
    </Formik>
  );
};
export const WorkflowAddAuth: React.FC = () => {
  const { hasModulePolicy } = useContext(UserDataContext);
  const hasRight = useMemo(() => {
    return (
      hasModulePolicy(ModuleNames.core, ModulePolicyTypes.entitytypesAdmin) &&
      hasModulePolicy(ModuleNames.core, ModulePolicyTypes.usergroupsAdmin) &&
      hasModulePolicy(ModuleNames.core, ModulePolicyTypes.workflowAdmin)
    );
  }, [hasModulePolicy]);
  return (
    <>
      {hasRight ? (
        <Container noHVContainer>
          <WorkflowAdd />{" "}
        </Container>
      ) : (
        <NotFound />
      )}
    </>
  );
};
export default WorkflowAdd;
